(function() {

    const sharedSelectionQueryKey = 'selectedTestIdents';
    const advisoryColorOrder = ['primary', 'secondary', 'info', 'success', 'warning', 'danger', 'gray'];

    const groupSelectionHintsByColor = (hints) => {
        const groups = new Map();

        for (const hint of hints) {
            const color = advisoryColorOrder.includes(hint.color) ? hint.color : 'primary';

            if (!groups.has(color)) {
                groups.set(color, []);
            }

            groups.get(color).push(hint);
        }

        return advisoryColorOrder
            .filter(color => groups.has(color))
            .map(color => ({color, hints: groups.get(color)}));
    };

    const ensureExternalStore = () => {
        if (!window.Alpine || 'function' !== typeof window.Alpine.store) {
            return null;
        }

        if (!window.Alpine.store('testIdentSelection')) {
            window.Alpine.store('testIdentSelection', {
                selected: [],
                descendantSummaryMap: {},
                capacityAvailable: {},
                nodes: {},
                selectedSelectionHints: [],
                confirmedSelectionHintKeys: [],
            });
        }

        return window.Alpine.store('testIdentSelection');
    };

    const externalStoreFactory = (config = {}) => ({
        mobileSummaryOpen: false,
        shareFeedback: '',
        shareFeedbackTimeout: null,
        workflowPriceValue: Number(config.workflowPriceValue) || 0,

        get selected() {
            return ensureExternalStore()?.selected ?? [];
        },

        get descendantSummaryMap() {
            return ensureExternalStore()?.descendantSummaryMap ?? {};
        },

        get capacityAvailable() {
            return ensureExternalStore()?.capacityAvailable ?? {};
        },

        get nodes() {
            return ensureExternalStore()?.nodes ?? {};
        },

        get selectedSelectionHints() {
            return ensureExternalStore()?.selectedSelectionHints ?? [];
        },

        get groupedSelectedSelectionHints() {
            return groupSelectionHintsByColor(this.selectedSelectionHints);
        },

        get appointmentRelevantSelectionHints() {
            return this.selectedSelectionHints
                .filter(hint => true === hint?.appointmentRelevant);
        },

        get groupedAppointmentRelevantSelectionHints() {
            return groupSelectionHintsByColor(this.appointmentRelevantSelectionHints);
        },

        get confirmedSelectionHintKeys() {
            return ensureExternalStore()?.confirmedSelectionHintKeys ?? [];
        },

        set confirmedSelectionHintKeys(keys) {
            ensureExternalStore()?.setConfirmedSelectionHintKeys?.(keys);
        },

        summaryTotal() {
            const selectedPrice = this.selected.reduce((total, id) => {
                const price = Number(this.descendantSummaryMap[id]?.price_value);

                return total + (Number.isFinite(price) ? price : 0);
            }, 0);

            return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
                style: 'currency',
                currency: 'EUR',
            }).format(selectedPrice + this.workflowPriceValue);
        },

        removeSelectedOption(id) {
            ensureExternalStore()?.removeSelectedOption?.(id);
        },

        sharedConfigurationUrl() {
            const url = new URL(window.location.href);
            const selected = [...new Set(this.selected.map(String))];

            url.searchParams.set(sharedSelectionQueryKey, selected.join(','));

            return url.toString();
        },

        async copySharedConfigurationUrl(url) {
            if (navigator.clipboard && 'function' === typeof navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(url);

                return;
            }

            const input = document.createElement('textarea');
            input.value = url;
            input.setAttribute('readonly', '');
            input.style.position = 'fixed';
            input.style.opacity = '0';
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            input.remove();
        },

        showShareFeedback(message) {
            this.shareFeedback = message;
            clearTimeout(this.shareFeedbackTimeout);
            this.shareFeedbackTimeout = setTimeout(() => {
                this.shareFeedback = '';
            }, 2500);
        },

        async shareConfiguration() {
            const url = this.sharedConfigurationUrl();

            if ('function' === typeof navigator.share) {
                try {
                    await navigator.share({
                        title: document.title,
                        url,
                    });
                    this.showShareFeedback('Geteilt');

                    return;
                } catch (error) {
                    if ('AbortError' === error?.name) {
                        return;
                    }
                }
            }

            try {
                await this.copySharedConfigurationUrl(url);
                this.showShareFeedback('Link kopiert');
            } catch (error) {
                this.showShareFeedback('Link konnte nicht kopiert werden');
            }
        },
    });

    // Factory for the Alpine component
    const factory = (config = {}) => ({

        /** Registry of checkbox nodes */
        nodes: {},

        /** Active selections (IDs; array so x-model works) */
        selected: [],

        /** Injected config maps */
        descendantMap: config.descendantMap || {},
        descendantKeyMap: config.descendantKeyMap || {},
        descendantSummaryMap: config.descendantSummaryMap || {},
        availableSelectionIds: [...new Set(
            (Array.isArray(config.availableSelectionIds)
                ? config.availableSelectionIds
                : Object.keys(config.descendantSummaryMap || {}))
                .map(String),
        )],
        selectionHints: config.selectionHints || {},
        confirmedSelectionHintKeys: [],
        categoryCatalogProfileMap: config.categoryCatalogProfileMap || {},
        categoryFilterOptions: Array.isArray(config.categoryFilterOptions) ? config.categoryFilterOptions : [],
        catalogProfileLayouts: config.catalogProfileLayouts || {},

        /**
         * testIdentKeyMap: { "<id>": "<key>" }
         * Example: { "2205": "vitd" }
         */
        testIdentKeyMap: config.testIdentKeyMap || {},

        /**
         * capacityAvailable:
         * {
         *   "2": { name: "Trockenblut", available: 100, required: 0 },
         *   "4": { name: "Kapillarblut", available: 100, required: 0 }
         * }
         */
        capacityAvailable: JSON.parse(
            JSON.stringify(config.capacityAvailable || {})),

        /**
         * capacityRequired:
         * {
         *   638: { 4: 1.5 },
         *   180: { 2: 1.0 },
         *   ...
         * }
         */
        capacityRequired: config.capacityRequired || {},

        userInteracted: false,

        /** Tracks last user toggled ID to resolve duplicates deterministically */
        lastToggledId: null,

        requiredSampleMaterial: null,

        /** ----- BADGE FILTER (SLUGS) ----- */

        /**
         * Active badge filter slugs (array so Alpine updates reliably)
         * Example: ["vitd", "omega3"]
         */
        activeSlugs: Array.isArray(config.activeSlugs) ? config.activeSlugs.map(String) : [],

        /**
         * Filter mode: "or" (any match) or "and" (must match all)
         */
        filterMode: ('and' === String(config.filterMode || '').toLowerCase()) ? 'and' : 'or',

        showOnlyFeaturedGroupOnStart: true === config.showOnlyFeaturedGroupOnStart,
        progressiveGroupLoading: true === config.progressiveGroupLoading,
        nonFeaturedGroupsLoaded: true === config.nonFeaturedGroupsLoaded,
        nonFeaturedGroupsLoading: false,
        nonFeaturedGroupsLoadPromise: null,
        nonFeaturedGroupsAutoLoadScheduled: false,
        showAllEntries: 'string' === typeof config.activeCatalogProfile && '' !== config.activeCatalogProfile,
        activeCatalogProfile: ('string' === typeof config.activeCatalogProfile && '' !== config.activeCatalogProfile)
            ? config.activeCatalogProfile
            : null,
        catalogSearchTerm: '',

        activeCatalogLayout() {
            if (!this.activeCatalogProfile) {
                return 'grouped';
            }

            return this.catalogProfileLayouts[String(this.activeCatalogProfile)] || 'grouped';
        },

        isCatalogLayout(layout) {
            return this.activeCatalogLayout() === String(layout);
        },

        normalizeCatalogSearchValue(value) {
            return String(value || '')
                .toLocaleLowerCase(document.documentElement.lang || 'de-DE')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '');
        },

        matchesCatalogSearch(searchableText) {
            const searchTerms = this.normalizeCatalogSearchValue(this.catalogSearchTerm)
                .trim()
                .split(/\s+/)
                .filter(Boolean);

            if (0 === searchTerms.length) {
                return true;
            }

            const normalizedSearchableText = this.normalizeCatalogSearchValue(searchableText);

            return searchTerms.every(searchTerm => normalizedSearchableText.includes(searchTerm));
        },

        dispatchActiveSlugsUpdated() {
            window.dispatchEvent(new CustomEvent('test-ident-active-slugs-updated', {
                detail: {
                    activeSlugs: [...this.activeSlugs],
                    availableCategorySlugs: this.availableCategorySlugs(),
                },
            }));
        },

        dispatchCatalogProfileUpdated() {
            window.dispatchEvent(new CustomEvent('test-ident-catalog-profile-updated'));
        },

        syncCatalogProfileInput() {
            const input = document.getElementById('test-ident-catalog-profile');

            if (!input) {
                return;
            }

            const value = this.activeCatalogProfile || '';

            if (input.value !== value) {
                input.value = value;
                input.dispatchEvent(new Event('input', {bubbles: true}));
                input.dispatchEvent(new Event('change', {bubbles: true}));
            }
        },

        hydrateSelectedFromCsvInput() {
            const input = this.$el?.querySelector('#selected-test-idents-csv')
                || document.getElementById('selected-test-idents-csv');
            const value = String(input?.value || '').trim();

            if ('' !== value) {
                this.selectedCsv = value;

                return;
            }

            const sharedSelection = this.sharedSelectionFromUrl();

            if (0 < sharedSelection.length) {
                this.selected = sharedSelection;
            }
        },

        sharedSelectionFromUrl() {
            try {
                const value = new URL(window.location.href).searchParams.get(sharedSelectionQueryKey);

                if (!value) {
                    return [];
                }

                return [...new Set(
                    value
                        .split(',')
                        .map(id => id.trim())
                        .filter(id => '' !== id)
                        .filter(id => Object.prototype.hasOwnProperty.call(this.descendantKeyMap, id)),
                )];
            } catch (error) {
                return [];
            }
        },

        syncSelectedCsvInput(value = this.selectedCsv) {
            const input = document.getElementById('selected-test-idents-csv');

            if (!input || input.value === value) {
                return;
            }

            input.value = value;
            input.dispatchEvent(new Event('input', {bubbles: true}));
            input.dispatchEvent(new Event('change', {bubbles: true}));
        },

        hydrateSelectionHintConfirmations() {
            const input = this.$el?.querySelector('#confirmed-advisory-keys')
                || document.getElementById('confirmed-advisory-keys');

            try {
                const keys = JSON.parse(String(input?.value || '[]'));

                this.confirmedSelectionHintKeys = Array.isArray(keys)
                    ? [...new Set(keys.filter(key => 'string' === typeof key && '' !== key))]
                    : [];
            } catch (error) {
                this.confirmedSelectionHintKeys = [];
            }
        },

        syncSelectionHintConfirmations() {
            const input = document.getElementById('confirmed-advisory-keys');

            if (!input) {
                return;
            }

            const value = JSON.stringify([...new Set(this.confirmedSelectionHintKeys)]);

            if (input.value === value) {
                return;
            }

            input.value = value;
            input.dispatchEvent(new Event('input', {bubbles: true}));
            input.dispatchEvent(new Event('change', {bubbles: true}));
        },

        ensureNonFeaturedGroupsLoaded() {
            if (!this.progressiveGroupLoading || this.nonFeaturedGroupsLoaded) {
                return Promise.resolve();
            }

            if (this.nonFeaturedGroupsLoadPromise) {
                return this.nonFeaturedGroupsLoadPromise;
            }

            if (!this.$wire || typeof this.$wire.loadExternalTestIdentGroups !== 'function') {
                this.nonFeaturedGroupsLoaded = true;

                return Promise.resolve();
            }

            this.nonFeaturedGroupsLoading = true;
            this.nonFeaturedGroupsLoadPromise = this.$wire.loadExternalTestIdentGroups()
                .then(() => {
                    this.nonFeaturedGroupsLoaded = true;
                })
                .finally(() => {
                    this.nonFeaturedGroupsLoading = false;
                    this.nonFeaturedGroupsLoadPromise = null;
                });

            return this.nonFeaturedGroupsLoadPromise;
        },

        scheduleNonFeaturedGroupsLoad() {
            if (
                !this.progressiveGroupLoading
                || this.nonFeaturedGroupsLoaded
                || this.nonFeaturedGroupsAutoLoadScheduled
            ) {
                return;
            }

            this.nonFeaturedGroupsAutoLoadScheduled = true;

            requestAnimationFrame(() => {
                setTimeout(() => {
                    this.ensureNonFeaturedGroupsLoaded().catch(() => {
                        this.nonFeaturedGroupsAutoLoadScheduled = false;
                    });
                }, 0);
            });
        },

        async setCatalogProfile(profile) {
            profile = String(profile || '').trim();

            if ('' !== profile) {
                await this.ensureNonFeaturedGroupsLoaded();
            }

            window.dispatchEvent(new CustomEvent('test-ident-catalog-profile-requested', {
                detail: {profile},
            }));
        },

        async searchCatalog(searchTerm) {
            searchTerm = String(searchTerm || '');

            if ('' !== this.normalizeCatalogSearchValue(searchTerm).trim()) {
                await this.ensureNonFeaturedGroupsLoaded();
            }

            window.dispatchEvent(new CustomEvent('test-ident-catalog-search-requested', {
                detail: {searchTerm},
            }));
        },

        applyCatalogSearch(searchTerm) {
            this.catalogSearchTerm = String(searchTerm || '');

            if ('' !== this.normalizeCatalogSearchValue(this.catalogSearchTerm).trim()) {
                this.showAllEntries = true;
            }
        },

        applyCatalogProfile(profile) {
            profile = String(profile || '').trim();

            this.activeCatalogProfile = '' !== profile ? profile : null;
            this.showAllEntries = null !== this.activeCatalogProfile;
            this.activeSlugs = [];

            this.normalize();
            this.recomputeCapacities();
            this.updateMaterialBars();
            this.updateCapacityDisabledState();
            this.updateRequiredSampleMaterial();
            this.syncCatalogProfileInput();
            this.dispatchActiveSlugsUpdated();
            this.dispatchCatalogProfileUpdated();
            this.publishExternalStore();
        },

        async toggleSlug(slug) {

            await this.ensureNonFeaturedGroupsLoaded();

            window.dispatchEvent(new CustomEvent('test-ident-slug-toggle-requested', {
                detail: {slug: String(slug)},
            }));
        },

        applyToggleSlug(slug) {

            slug = String(slug);
            this.showAllEntries = false;

            if (true === this.activeSlugs.includes(slug)) {
                this.activeSlugs = this.activeSlugs.filter(v => v !== slug);
                this.dispatchActiveSlugsUpdated();
                return;
            }

            this.activeSlugs.push(slug);
            this.dispatchActiveSlugsUpdated();
        },

        setActiveSlugs(slugs) {
            this.activeSlugs = Array.isArray(slugs)
                ? [...new Set(slugs.map(String))]
                : [];
            this.showAllEntries = false;
            this.dispatchActiveSlugsUpdated();
        },

        async showAllTestIdents() {
            await this.ensureNonFeaturedGroupsLoaded();

            window.dispatchEvent(new CustomEvent('test-ident-show-all-requested'));
        },

        applyShowAllTestIdents() {
            this.showAllEntries = true;
            this.clearSlugs();
        },

        isSlugActive(slug) {
            return true === this.activeSlugs.includes(String(slug));
        },

        isVirtualOptionDisabled(key) {
            const node = this.nodes[String(key)];

            return !node || true === node.el.disabled;
        },

        toggleVirtual(key) {
            key = String(key);

            const node = this.nodes[key];

            if (!node || true === node.el.disabled || true === node.preselected) {
                return;
            }

            node.el.checked = !node.el.checked;
            node.el.dispatchEvent(new Event('input', {bubbles: true}));
            node.el.dispatchEvent(new Event('change', {bubbles: true}));
            this.toggle(key);
        },

        matchesCatalogProfile(profileSlugs) {

            if (!this.activeCatalogProfile) {
                return true;
            }

            profileSlugs = Array.isArray(profileSlugs) ? profileSlugs.map(String) : [];

            return profileSlugs.includes(String(this.activeCatalogProfile));
        },

        matchesCategoryCatalogProfile(slug) {

            if (!this.activeCatalogProfile) {
                return true;
            }

            const profileSlugs = this.categoryCatalogProfileMap[String(slug)] || [];

            if (false === Array.isArray(profileSlugs) || 0 === profileSlugs.length) {
                return false;
            }

            return profileSlugs.map(String).includes(String(this.activeCatalogProfile));
        },

        categoryOptionSlugs() {
            return this.categoryFilterOptions.map(option => ({
                categorySlugs: Array.isArray(option.category_slugs)
                    ? option.category_slugs.map(String)
                    : [],
                catalogProfileSlugs: Array.isArray(option.catalog_profile_slugs)
                    ? option.catalog_profile_slugs.map(String)
                    : [],
            }));
        },

        hasMatchingCategoryCombination(slug) {
            slug = String(slug);

            if (this.isSlugActive(slug)) {
                return true;
            }

            const requiredSlugs = [...new Set([...this.activeSlugs, slug])];

            return this.categoryOptionSlugs().some(({categorySlugs, catalogProfileSlugs}) => (
                this.matchesCatalogProfile(catalogProfileSlugs)
                && requiredSlugs.every(requiredSlug => categorySlugs.includes(requiredSlug))
            ));
        },

        availableCategorySlugs() {
            return [...new Set(
                this.categoryOptionSlugs()
                    .flatMap(({categorySlugs}) => categorySlugs)
                    .filter(slug => this.hasMatchingCategoryCombination(slug)),
            )];
        },

        clearSlugs() {
            this.activeSlugs = [];
            this.dispatchActiveSlugsUpdated();
        },

        /**
         * Decide if an item with given slugs should be visible
         * - If 0 active slugs -> show all
         * - OR: itemSlugs intersects activeSlugs
         * - AND: itemSlugs contains all activeSlugs
         */
        matchesSlugs(itemSlugs) {

            if (0 === this.activeSlugs.length) {
                return true;
            }

            itemSlugs = Array.isArray(itemSlugs) ? itemSlugs.map(String) : [];

            if ('and' === this.filterMode) {
                return this.activeSlugs.every(s => true === itemSlugs.includes(String(s)));
            }

            // Default: OR
            return itemSlugs.some(s => true === this.activeSlugs.includes(String(s)));
        },

        shouldShowBySlugs(itemSlugs, groupIndex = null) {

            if (0 < this.activeSlugs.length) {
                return this.matchesSlugs(itemSlugs);
            }

            if (true === this.showAllEntries) {
                return true;
            }

            if (true === this.showOnlyFeaturedGroupOnStart) {
                return 0 === Number(groupIndex);
            }

            return this.matchesSlugs(itemSlugs);
        },

        shouldShowGroupByFilterOptions(filterOptions, groupIndex = null) {
            const hasMatchingOption = Array.isArray(filterOptions)
                && filterOptions.some(option => (
                    this.matchesCatalogProfile(option.catalogProfileSlugs)
                    && this.matchesSlugs(option.categorySlugs)
                    && this.matchesCatalogSearch(option.searchableText)
                ));

            if (!hasMatchingOption) {
                return false;
            }

            if (0 < this.activeSlugs.length || true === this.showAllEntries) {
                return true;
            }

            return !this.showOnlyFeaturedGroupOnStart || 0 === Number(groupIndex);
        },

        /**
         * Read slugs from a DOM element:
         * <div data-slugs="vitd,omega3"></div>
         */
        matchesElementByDataSlugs(el) {

            if (!el) {
                return true;
            }

            const raw = String(el.dataset?.slugs || '').trim();

            const itemSlugs = '' === raw
                ? []
                : raw.split(',').map(v => v.trim()).filter(v => '' !== v);

            return this.matchesSlugs(itemSlugs);
        },

        /** ----- EXISTING INIT/WATCHERS ----- */

        initGlobal() {
            this.hydrateSelectedFromCsvInput();
            this.hydrateSelectionHintConfirmations();
            this.publishExternalStore();
            this.syncCatalogProfileInput();

            if (0 < this.activeSlugs.length) {
                this.dispatchActiveSlugsUpdated();
            }

            if (this.activeCatalogProfile) {
                this.dispatchCatalogProfileUpdated();
            }

            this.$watch('selectedCsv', value => {
                this.syncSelectedCsvInput(value);

                // this.$wire.set('data.extra_attributes.selected_test_idents_csv', value);
                this.publishExternalStore();
            });

            this.$watch('confirmedSelectionHintKeys', () => {
                this.syncSelectionHintConfirmations();
                this.publishExternalStore();
            });

            this.syncSelectedCsvInput();
            this.syncSelectionHintConfirmations();

            this.$watch('requiredSampleMaterial', value => {

                const input = document.getElementById(
                    'required-sample-material');

                if (!input) {
                    return;
                }

                if (input.value !== value) {
                    input.value = value;
                    input.dispatchEvent(new Event('input', {bubbles: true}));
                    input.dispatchEvent(new Event('change', {bubbles: true}));
                }

                // this.$wire.set('data.extra_attributes.required_sample_material',
                //     value);
                this.publishExternalStore();
            });

            this.scheduleNonFeaturedGroupsLoad();
        },

        publishExternalStore() {
            const store = ensureExternalStore();

            if (!store) {
                return;
            }

            store.selected = this.selected;
            store.descendantSummaryMap = this.descendantSummaryMap;
            store.capacityAvailable = this.capacityAvailable;
            store.nodes = this.nodes;
            store.selectedSelectionHints = this.selectedSelectionHints;
            store.confirmedSelectionHintKeys = this.confirmedSelectionHintKeys;
            store.setConfirmedSelectionHintKeys = (keys) => {
                this.confirmedSelectionHintKeys = Array.isArray(keys)
                    ? [...new Set(keys.map(String).filter(key => '' !== key))]
                    : [];
            };
            store.removeSelectedOption = (key) => this.removeSelectedOption(key);
        },

        get missingSelection() {
            return true === this.userInteracted && 0 === this.selected.length;
        },

        get selectedCsv() {
            return this.selected.join(',');
        },

        filterAvailableSelections(selectedIds) {
            if (!Array.isArray(selectedIds)) {
                return [];
            }

            return [...new Set(
                selectedIds
                    .map(String)
                    .filter(id => this.availableSelectionIds.includes(id)),
            )];
        },

        set selectedCsv(val) {
            const selectedIds = val
                ? String(val).split(',').map(v => v.trim()).filter(v => '' !== v)
                : [];

            this.selected = this.filterAvailableSelections(selectedIds);
        },

        get selectedSelectionHints() {
            const hintsByKey = new Map();

            for (const testIdentId of this.selected.map(String)) {
                const hints = this.selectionHints?.[testIdentId] || [];

                if (!Array.isArray(hints)) {
                    continue;
                }

                for (const hint of hints) {
                    if (!hint || 'string' !== typeof hint.key || '' === hint.key) {
                        continue;
                    }

                    const existingHint = hintsByKey.get(hint.key);

                    if (!existingHint || Number(hint.priority) < Number(existingHint.priority)) {
                        hintsByKey.set(hint.key, hint);
                    }
                }
            }

            return [...hintsByKey.values()]
                .sort((left, right) => {
                    const priorityComparison = Number(left.priority) - Number(right.priority);

                    return 0 !== priorityComparison
                        ? priorityComparison
                        : String(left.key).localeCompare(String(right.key));
                });
        },

        get groupedSelectedSelectionHints() {
            return groupSelectionHintsByColor(this.selectedSelectionHints);
        },

        /** Selection helpers */

        isSelected(key) {
            return true === this.selected.includes(String(key));
        },

        addSelected(key) {
            key = String(key);
            if (false === this.isSelected(key)) {
                this.selected.push(key);
            }
        },

        removeSelected(key) {
            key = String(key);

            const wasSelected = this.isSelected(key);
            const itemKey = this.toKeyById(key);

            this.selected = this.selected.filter(v => v !== key);

            const hasSelectedItemKey = this.selected.some(
                selectedKey => this.toKeyById(selectedKey) === itemKey,
            );

            if (wasSelected && !hasSelectedItemKey) {
                window.dispatchEvent(new CustomEvent('test-ident-selection-removed', {
                    detail: {itemKey},
                }));
            }
        },

        removeSelectedOption(key) {

            key = String(key);

            const node = this.nodes[key];

            if (!node) {
                this.removeSelected(key);
                this.normalize();
                this.recomputeCapacities();
                this.updateMaterialBars();
                this.updateCapacityDisabledState();
                this.updateRequiredSampleMaterial();
                this.publishExternalStore();
                return;
            }

            if (true === node.preselected) {
                return;
            }

            node.el.checked = false;
            node.el.dispatchEvent(new Event('input', {bubbles: true}));
            node.el.dispatchEvent(new Event('change', {bubbles: true}));
            this.toggle(key);
        },

        /**
         * Map an ID to its semantic key (e.g. "vitd").
         * Fallback: return the ID itself if mapping missing.
         */
        toKeyById(id) {
            id = String(id);

            const key = this.testIdentKeyMap?.[id];

            return (typeof key === 'string' && '' !== key)
                ? String(key)
                : id;
        },

        /**
         * Return all node IDs that represent the given semantic key.
         * This covers cases where the same test exists with multiple IDs.
         */
        getNodeIdsByKey(targetKey) {
            targetKey = String(targetKey);

            const ids = [];
            for (const id in this.nodes) {
                if (this.toKeyById(id) === targetKey) {
                    ids.push(String(id));
                }
            }

            return ids;
        },

        /**
         * Register checkbox
         */
        register(key, el, descendants, isPreselected = false, catalogProfileSlugs = []) {

            key = String(key);

            this.nodes[key] = {
                el,
                descendants: Array.isArray(descendants) ? descendants.map(
                    String) : [],
                preselected: !!isPreselected,
                catalogProfileSlugs: Array.isArray(catalogProfileSlugs)
                    ? catalogProfileSlugs.map(String)
                    : [],
            };

            const wrapper = el.closest('.button-option');

            if (true === isPreselected) {
                // Preselected nodes stay fixed
                el.checked = true;
                el.disabled = true;

                if (wrapper) {
                    wrapper.classList.add('disabled', 'preselected', 'selected');
                }

                this.addSelected(key);

            } else if (true === el.checked) {
                this.addSelected(key);
            } else if (true === this.isSelected(key)) {
                el.checked = true;
            }

            this.normalize();
            this.recomputeCapacities();
            this.updateMaterialBars();
            this.updateCapacityDisabledState();
            this.updateRequiredSampleMaterial();
            this.publishExternalStore();
        },

        /**
         * Handle user interaction
         */
        toggle(key) {

            this.userInteracted = true;

            key = String(key);
            this.lastToggledId = key;

            const node = this.nodes[key];
            if (!node) {
                return;
            }

            // Preselected nodes are fixed
            if (true === node.preselected) {
                node.el.checked = true;
                return;
            }

            if (true === node.el.checked) {
                this.addSelected(key);
            } else {
                this.removeSelected(key);
            }

            this.normalize();
            this.recomputeCapacities();
            this.updateMaterialBars();
            this.updateCapacityDisabledState();
            this.updateRequiredSampleMaterial();
            this.publishExternalStore();
        },

        /**
         * Graph + overlap normalization:
         * - Enforce uniqueness by semantic key (e.g. "vitd")
         * - Remove selected children if parent selected (graph descendants by ID)
         * - Reset disabled state
         * - Disable graph descendants (by ID)
         * - Apply overlap/exclusion via descendantKeyMap (KEYS!)
         * - Never remove or modify preselected items
         */
        normalize() {

            const snapshot = [...this.selected];

            /**
             * Step 0: enforce uniqueness by semantic key.
             * Keep exactly one ID per key.
             *
             * Priority:
             * 1) preselected
             * 2) last toggled
             * 3) first seen
             */
            const chosenByKey = new Map(); // key -> id

            for (const id of snapshot) {

                const key = this.toKeyById(id);

                if (false === chosenByKey.has(key)) {
                    chosenByKey.set(key, id);
                    continue;
                }

                const currentId = chosenByKey.get(key);
                const currentNode = this.nodes[currentId];
                const candidateNode = this.nodes[id];

                // Preselected wins
                if (true === (candidateNode?.preselected ?? false) && false ===
                    (currentNode?.preselected ?? false)) {
                    chosenByKey.set(key, id);
                    continue;
                }

                // Otherwise: keep last toggled if it matches
                if (
                    null !== this.lastToggledId
                    && String(id) === String(this.lastToggledId)
                    && false === (candidateNode?.preselected ?? false)
                ) {
                    chosenByKey.set(key, id);
                }
            }

            // Remove non-chosen duplicates
            for (const id of snapshot) {

                const key = this.toKeyById(id);
                const keepId = chosenByKey.get(key);

                if (String(id) === String(keepId)) {
                    continue;
                }

                const node = this.nodes[id];
                if (node && false === node.preselected) {

                    node.el.checked = false;
                    node.el.dispatchEvent(new Event('input', {bubbles: true}));
                    node.el.dispatchEvent(new Event('change', {bubbles: true}));

                    this.removeSelected(id);
                }
            }

            /**
             * Step 1: remove child selections if a parent is selected
             */
            const snapshotAfterDedupe = [...this.selected];

            for (const a of snapshotAfterDedupe) {
                for (const b of snapshotAfterDedupe) {

                    if (a === b) {
                        continue;
                    }

                    const bDesc = this.nodes[b]?.descendants || [];
                    if (true === bDesc.includes(a)) {

                        const nodeA = this.nodes[a];

                        if (nodeA && false === nodeA.preselected) {

                            nodeA.el.checked = false;
                            nodeA.el.dispatchEvent(
                                new Event('input', {bubbles: true}));
                            nodeA.el.dispatchEvent(
                                new Event('change', {bubbles: true}));

                            this.removeSelected(a);
                        }
                    }
                }
            }

            /**
             * Step 2: reset all nodes
             */
            Object.values(this.nodes).forEach(n => {
                if (true === n.preselected) {
                    return;
                }
                this.toggleNode(n, false);
            });

            /**
             * Step 3: disable graph descendants
             */
            for (const parent of this.selected) {

                const desc = this.nodes[parent]?.descendants || [];

                for (const childId of desc) {

                    const child = this.nodes[childId];
                    if (!child || true === child.preselected) {
                        continue;
                    }

                    child.el.checked = false;
                    this.removeSelected(childId);

                    this.toggleNode(child, true, true);
                }
            }

            /**
             * Step 4: apply overlap/exclusion via descendantKeyMap (KEYS!)
             *
             * descendantKeyMap[parentId] = ["holotc","vitd","feri",...]
             * For each excluded key: disable all node IDs that have that key.
             */
            const excludedKeys = new Set();

            for (const parentId of this.selected) {

                const keys = this.descendantKeyMap?.[String(parentId)] || [];
                if (false === Array.isArray(keys)) {
                    continue;
                }

                for (const k of keys) {
                    if ('string' === typeof k && '' !== k) {
                        excludedKeys.add(
                            String(k));
                    }
                }
            }

            for (const exKey of excludedKeys) {

                // Prevent self-lock if a mapping ever includes the same key as selected
                const selectedHasSameKey = this.selected.some(
                    id => this.toKeyById(id) === exKey);

                if (true === selectedHasSameKey) {
                    continue;
                }

                const ids = this.getNodeIdsByKey(exKey);

                for (const id of ids) {

                    const node = this.nodes[id];
                    if (!node || true === node.preselected) {
                        continue;
                    }

                    node.el.checked = false;
                    this.removeSelected(id);

                    this.toggleNode(node, true, true);
                }
            }

            /**
             * Step 4b: disable "panel" nodes whose descendantKeyMap is fully covered
             * by excluded keys (e.g. selecting 2205 disables 2509 if 2509 == [trig,ldl,hdl,CHOL]).
             */
            for (let descendantId in this.descendantKeyMap) {

                descendantId = String(descendantId);

                if (this.lastToggledId === descendantId) {
                    continue;
                }

                const candidateNode = this.nodes?.[descendantId];

                if (!candidateNode) {
                    continue;
                }

                if (true === (candidateNode.preselected ?? false)) {
                    continue;
                }

                const candidateKeys = this.descendantKeyMap?.[descendantId];

                if (false === Array.isArray(candidateKeys) || 0 === candidateKeys.length) {
                    continue;
                }

                const candidateExcludedKeys = true === this.isSelected(descendantId)
                    ? new Set(
                        this.selected
                            .filter(selectedId => String(selectedId) !== descendantId)
                            .flatMap(selectedId => this.descendantKeyMap?.[
                                String(selectedId)] || []),
                    )
                    : excludedKeys;

                // A selected package is redundant only when its values are covered by
                // other selected packages. For unselected packages, all selected values
                // form the exclusion set.
                let isFullyExcluded = true;

                for (const k of candidateKeys) {

                    if ('string' !== typeof k || '' === k) {
                        continue;
                    }

                    if (false === candidateExcludedKeys.has(String(k))) {
                        isFullyExcluded = false;
                        break;
                    }
                }

                if (false === isFullyExcluded) {
                    continue;
                }

                // Deselect covered packages and disable unavailable options.
                candidateNode.el.checked = false;
                this.removeSelected(descendantId);

                this.toggleNode(candidateNode, true, true);
            }
        },

        /** Disable / enable one node (input + wrapper styling) */
        toggleNode(node, disabled, selected = false) {

            // Preselected nodes always stay disabled and styled
            if (true === node.preselected) {

                node.el.disabled = true;

                const wrapper = node.el.closest('.button-option');

                if (wrapper) {
                    wrapper.classList.add('disabled', 'preselected');
                }

                return;
            }

            node.el.disabled = disabled;

            const wrapper = node.el.closest('.button-option');

            if (wrapper) {

                wrapper.classList.remove('selected');
                wrapper.classList.toggle('disabled', disabled);

                if (true === selected || node.el.checked) {
                    wrapper.classList.add('selected');
                }
            }
        },

        /** ----- MATERIAL CALCULATION ----- */

        recomputeCapacities() {

            // Reset all required values
            for (const id in this.capacityAvailable) {
                this.capacityAvailable[id].required = 0;
            }

            // Sum requirements for selected TestIdents
            for (const testId of this.selected) {

                const req = this.capacityRequired[testId] || {};

                for (const matId in req) {

                    const amount = parseFloat(req[matId]) || 0;
                    if (!this.capacityAvailable[matId]) {
                        continue;
                    }

                    this.capacityAvailable[matId].required += amount;
                }
            }

            // Clamp
            for (const id in this.capacityAvailable) {

                const cap = this.capacityAvailable[id];

                if (cap.required < 0) {
                    cap.required = 0;
                }

                if (cap.available > 0 && cap.required > cap.available) {
                    cap.required = cap.available;
                }
            }
        },

        updateMaterialBars() {

            const container = document.querySelector('.sample-material-info');
            if (!container) {
                return;
            }

            container.querySelectorAll('[data-material-id]').forEach(row => {

                const materialId = row.getAttribute('data-material-id');
                const mat = this.capacityAvailable[materialId];
                if (!mat) {
                    return;
                }

                const percent = mat.available > 0
                    ? Math.min((mat.required * 100) / mat.available, 100)
                    : 0;

                const bar = row.querySelector('.sample-bar-fill');
                if (bar) {
                    bar.style.width = percent + '%';
                }

                row.querySelectorAll('[data-seg]').forEach(seg => {

                    const index = Number(seg.getAttribute('data-seg'));

                    seg.classList.toggle('text-white', index <= mat.required);
                    seg.classList.toggle('text-gray-500', index > mat.required);
                });
            });
        },

        isOptionAllowedByCapacity(key) {

            key = String(key);

            const req = this.capacityRequired[key];
            if (!req) {
                return true;
            }

            for (const matId in req) {

                const needed = parseFloat(req[matId]) || 0;
                const mat = this.capacityAvailable[matId];
                if (!mat) {
                    continue;
                }

                const remaining = mat.available - mat.required;

                if (remaining < needed) {
                    return false;
                }
            }

            return true;
        },

        updateCapacityDisabledState() {

            for (const key in this.nodes) {

                const node = this.nodes[key];

                // Preselected nodes always disabled & styled
                if (true === node.preselected) {
                    node.el.checked = true;
                    this.toggleNode(node, true);
                    continue;
                }

                // Selected items must stay enabled
                if (true === this.isSelected(key)) {
                    this.toggleNode(node, false);
                    continue;
                }

                // If already disabled by graph logic → do not override
                if (true === node.el.disabled) {
                    continue;
                }

                const allowed = this.isOptionAllowedByCapacity(key);
                this.toggleNode(node, !allowed);
            }
        },

        updateRequiredSampleMaterial() {

            // Track which material IDs are needed
            const requiredMaterialIds = new Set();

            // Accumulate from the selected test idents
            for (const testId of this.selected) {

                const reqCaps = this.capacityRequired[testId] || {};

                for (const matId in reqCaps) {
                    requiredMaterialIds.add(String(matId));
                }
            }

            this.requiredSampleMaterial = [...requiredMaterialIds].sort().
                join('');
        },

    });

    window.testIdentSelectionGlobal = factory;
    window.testIdentSelectionExternalStore = externalStoreFactory;

    // Safe registration
    if (window.Alpine) {
        window.Alpine.data('testIdentSelectionGlobal', factory);
        window.Alpine.data('testIdentSelectionExternalStore', externalStoreFactory);
    } else {
        document.addEventListener('alpine:init', () => {
            window.Alpine.data('testIdentSelectionGlobal', factory);
            window.Alpine.data('testIdentSelectionExternalStore', externalStoreFactory);
        });
    }
})();
